/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  formattedDate,
  isObjectOrArrayAndEmpty
} from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  ICarbonAvoidanceData,
  IDetailComponentProps,
  IRequestContext,
  ISiteCard
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card } from '@scuf/common';
import CarbonAvoidanceSummaryComponent from './CarbonAvoidanceSummaryComponent';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import useMounted from '../../../shared/useMount/useMount';

interface ISummaryComponentProps {
  bearerToken: string;
  requestContext: IRequestContext;
}

const getConnectorConfigCarbon = () => {
  return {
    name: 'portfolio-carbon-avoidance',
    options: {
      startDate: '',
      endDate: ''
    }
  };
};

interface ITotalEmissionDetailContent extends ISiteCard {
  data: ICarbonAvoidanceData;
}

const CarbonAvoidanceSummaryContent = (props: ITotalEmissionDetailContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        <CarbonAvoidanceSummaryComponent data={data} />
      </div>
    </div>
  );
};

const CarbonAvoidanceSummaryWithLoader = withLoadingStatus(
  withErrorStatus(CarbonAvoidanceSummaryContent)
);

const CarbonAvoidanceSummaryWidget = (props: ISummaryComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.DetailedView;
  const e = descriptors.energyManagement;
  const { bearerToken, requestContext } = props;
  const isMounted = useMounted();
  const connectorConfig = getConnectorConfigCarbon();
  connectorConfig.options.startDate = formattedDate(
    requestContext.context.startTime ?? ''
  );
  connectorConfig.options.endDate = formattedDate(
    requestContext.context.endTime ?? ''
  );

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  const { all, loading, error, status } = dataConnector.data;
  const carbonTotalEmissionsData = (all as unknown) as ICarbonAvoidanceData;
  const isDataAvailable = carbonTotalEmissionsData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonAvoidanceData);

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [requestContext]);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(carbonTotalEmissionsData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header className="emaas-carbon-view-details-total-emissions-savings-meter-card-header">
          {t(d.carbonEmissionsAvoidance)}
        </Card.Header>
        <Card.Content className="detail-summary-card detail-view">
          <CarbonAvoidanceSummaryWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(e.LOADING)}
            title={t(e.CONNECTIONERRORTITLE)}
            noDataMessage={t(e.NODATAAVAILABLE)}
            connectionErrorMessage={t(e.CONNECTIONERROR)}
          />
        </Card.Content>
      </Card>
    </>
  );
};

export default CarbonAvoidanceSummaryWidget;
